import React, { useEffect, useId, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ChartIcon } from './icons/DashboardIcons';
import type { ChartSeriesEmphasis } from '../utils/chartStats';
import './LiveStatsChart.css';

interface ChartDataPoint {
  time: string;
  [key: string]: number | string;
}

interface DataKeyConfig {
  key: string;
  name: string;
  color: string;
  yAxisId?: 'left' | 'right';
  emphasis?: ChartSeriesEmphasis;
}

interface LiveStatsChartProps {
  data: ChartDataPoint[];
  dataKeys: DataKeyConfig[];
  title?: string;
  height?: number;
  showArea?: boolean;
  /** Smooth area (default) or stepped waveform */
  curve?: 'monotone' | 'step';
  valueScale?: Record<string, number>;
}

const EMPHASIS_STYLE: Record<
  ChartSeriesEmphasis,
  { strokeWidth: number; strokeOpacity: number; fillTop: number; fillBottom: number; dot: boolean; activeR: number }
> = {
  muted:  { strokeWidth: 1.25, strokeOpacity: 1,    fillTop: 0.08, fillBottom: 0,    dot: false, activeR: 3 },
  normal: { strokeWidth: 2.5,  strokeOpacity: 1,    fillTop: 0.22, fillBottom: 0,    dot: false, activeR: 4 },
  primary:{ strokeWidth: 3.5,  strokeOpacity: 1,    fillTop: 0.42, fillBottom: 0.02, dot: true,  activeR: 6 },
  super:  { strokeWidth: 4.5,  strokeOpacity: 1,    fillTop: 0.55, fillBottom: 0.04, dot: true,  activeR: 8 },
  // dots : pas de ligne visible, uniquement des petits points sur l'axe secondaire
  dots:   { strokeWidth: 0,    strokeOpacity: 0,    fillTop: 0,    fillBottom: 0,    dot: true,  activeR: 4 },
};

const SuperHitDot = ({ cx, cy, payload, dataKey }: any) => {
  const raw = payload?.[`__raw_${dataKey}`] ?? payload?.[dataKey];
  if (cx == null || cy == null || !raw || raw <= 0) return null;

  return (
    <g>
      <circle cx={cx} cy={cy} r={10} fill="rgba(255,229,102,0.12)" />
      <circle cx={cx} cy={cy} r={6} fill="rgba(255,229,102,0.28)" />
      <circle cx={cx} cy={cy} r={3.5} fill="#ffe566" stroke="#0a0a0a" strokeWidth={1.5} />
    </g>
  );
};

const ValidHitDot = ({ cx, cy, payload, dataKey }: any) => {
  const raw = payload?.[`__raw_${dataKey}`] ?? payload?.[dataKey];
  if (cx == null || cy == null || !raw || raw <= 0) return null;
  return (
    <circle cx={cx} cy={cy} r={2.75} fill="#e53935" stroke="#0a0a0a" strokeWidth={1.25} />
  );
};

/** Dot générique coloré pour l'emphase 'dots' — radius adapté selon type */
const ColoredDot = ({ cx, cy, payload, dataKey, color, r = 3 }: any) => {
  const raw = payload?.[`__raw_${dataKey}`] ?? payload?.[dataKey];
  if (cx == null || cy == null || !raw || raw <= 0) return null;
  return (
    <circle cx={cx} cy={cy} r={r} fill={color} stroke="#0a0a0a" strokeWidth={0.8} fillOpacity={0.9} />
  );
};

const LiveStatsChart: React.FC<LiveStatsChartProps> = ({
  data,
  dataKeys,
  title,
  height = 300,
  showArea = false,
  curve = 'step',
  valueScale = {},
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const chartId = useId().replace(/:/g, '');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scaledData = useMemo(
    () =>
      data.map((point) => {
        const scaled: ChartDataPoint = { ...point };
        Object.entries(valueScale).forEach(([key, scale]) => {
          const raw = point[key];
          if (typeof raw === 'number' && scale > 1) {
            scaled[`__raw_${key}`] = raw;
            scaled[key] = raw * scale;
          }
        });
        return scaled;
      }),
    [data, valueScale]
  );

  const hasRightAxis = dataKeys.some((k) => k.yAxisId === 'right');
  const scaledHitKeys = new Set(Object.keys(valueScale));

  const formatAxisValue = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const HIT_EVENT_KEYS = new Set(['superHitEvent', 'validHitEvent', 'invalidHitEvent']);
    const ordered = [...payload].sort((a, b) => {
      const rank: Record<string, number> = { superHitEvent: 0, validHitEvent: 1, invalidHitEvent: 2 };
      return (rank[a.dataKey] ?? 9) - (rank[b.dataKey] ?? 9);
    });

    return (
      <div className="lsc-tooltip">
        <p className="lsc-tooltip-label">{label}</p>
        {ordered.map((entry: any, index: number) => {
          if (!HIT_EVENT_KEYS.has(entry.dataKey)) {
            // Ligne URLs traitées — valeur numérique simple
            const scale      = valueScale[entry.dataKey] ?? 1;
            const payloadPt  = entry.payload as ChartDataPoint;
            const rawKey     = `__raw_${entry.dataKey}`;
            const rawValue   =
              typeof payloadPt?.[rawKey] === 'number'
                ? (payloadPt[rawKey] as number)
                : scale > 1 ? entry.value / scale : entry.value;
            return (
              <div key={index} className="lsc-tooltip-row">
                <span className="lsc-tooltip-dot" style={{ backgroundColor: entry.color }} />
                <span className="lsc-tooltip-value">
                  {entry.name}: {rawValue.toLocaleString('en-US')}
                </span>
              </div>
            );
          }

          // Event-dots — n'afficher que si event = 1
          if (!entry.value || entry.value <= 0) return null;

          const isValid = entry.dataKey === 'validHitEvent';
          const isSuper = entry.dataKey === 'superHitEvent';

          // Pour les hits valides, afficher le service détecté
          const labels: string =
            isValid ? (entry.payload?.validHitLabels ?? '') : '';
          const detail = labels.length > 0 ? labels : null;

          return (
            <div key={index}
              className={`lsc-tooltip-row ${isSuper ? 'lsc-tooltip-row--super' : ''} ${isValid ? 'lsc-tooltip-row--valid' : ''}`}>
              <span className="lsc-tooltip-dot" style={{ backgroundColor: entry.color }} />
              <span className="lsc-tooltip-value">
                {entry.name}
                {detail && (
                  <span className="lsc-tooltip-detail"> — {detail}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const axisStyle = { fontSize: '10px', fontFamily: 'JetBrains Mono, ui-monospace, monospace' };
  const tickStyle = { fill: '#737373' };

  const renderSeries = (SeriesComponent: typeof Area | typeof Line) =>
    dataKeys.map(({ key, name, color, yAxisId = 'left', emphasis = 'normal' }) => {
      const style  = EMPHASIS_STYLE[emphasis];
      const isArea = SeriesComponent === Area;
      const isDots = emphasis === 'dots';

      const dotRenderer =
        isDots
          ? (props: any) => <ColoredDot {...props} dataKey={key} color={color} r={3} />
          : emphasis === 'super'
            ? (props: any) => <SuperHitDot {...props} dataKey={key} />
            : emphasis === 'primary'
              ? (props: any) => <ValidHitDot {...props} dataKey={key} />
              : false;

      // Pour les dots événementiels : activeDot masqué si value = 0
      const activeDotRenderer = isDots
        ? (props: any) => {
            const val = props?.payload?.[key];
            if (!val || val <= 0) return <g />;
            return <circle cx={props.cx} cy={props.cy} r={5} fill={color} stroke="#0a0a0a" strokeWidth={1} />;
          }
        : { r: style.activeR, fill: color, stroke: emphasis === 'super' ? '#fff' : '#0a0a0a', strokeWidth: emphasis === 'super' ? 2 : 1 };

      return (
        <SeriesComponent
          key={key}
          yAxisId={yAxisId}
          type={curve}
          dataKey={key}
          name={name}
          stroke={color}
          strokeWidth={style.strokeWidth}
          strokeOpacity={emphasis === 'muted' ? 0.55 : style.strokeOpacity}
          fill={isArea && !isDots ? `url(#${chartId}-grad-${key})` : 'none'}
          dot={style.dot ? dotRenderer : false}
          activeDot={activeDotRenderer}
          isAnimationActive={false}
          className={`lsc-series lsc-series--${emphasis}`}
        />
      );
    });

  const chartContent = showArea ? (
    <AreaChart data={scaledData} margin={{ top: 12, right: hasRightAxis ? 16 : 8, left: 0, bottom: 0 }}>
      <defs>
        {dataKeys.map(({ key, color, emphasis = 'normal' }) => {
          const style = EMPHASIS_STYLE[emphasis];
          return (
            <linearGradient key={key} id={`${chartId}-grad-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={style.fillTop} />
              <stop offset="95%" stopColor={color} stopOpacity={style.fillBottom} />
            </linearGradient>
          );
        })}
        <filter id={`${chartId}-super-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.04)" vertical={!showArea} />
      <XAxis dataKey="time" stroke="rgba(255,255,255,0.08)" style={axisStyle} tick={tickStyle} />
      <YAxis
        yAxisId="left"
        stroke="rgba(255,255,255,0.06)"
        style={axisStyle}
        tick={tickStyle}
        tickFormatter={formatAxisValue}
        width={48}
      />
      {/* Axe droit supprimé — tous les dots sont maintenant sur l'axe gauche, au niveau de la ligne */}
      <Tooltip content={<CustomTooltip />} />
      <Legend
        wrapperStyle={{ paddingTop: '12px' }}
        iconType="circle"
        formatter={(value, entry: any) => (
          <span
            className={`lsc-legend ${
              entry?.dataKey === 'superHitEvent'
                ? 'lsc-legend--super'
                : entry?.dataKey === 'validHitEvent'
                  ? 'lsc-legend--valid'
                  : ''
            }`}
          >
            {value}
          </span>
        )}
      />
      {renderSeries(Area)}
    </AreaChart>
  ) : (
    <LineChart data={scaledData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="2 6" stroke="rgba(255,255,255,0.04)" vertical={!showArea} />
      <XAxis dataKey="time" stroke="rgba(255,255,255,0.08)" style={axisStyle} tick={tickStyle} />
      <YAxis yAxisId="left" stroke="rgba(255,255,255,0.06)" style={axisStyle} tick={tickStyle} tickFormatter={formatAxisValue} width={48} />
      {/* Axe droit supprimé — tous les dots sont sur l'axe gauche */}
      <Tooltip content={<CustomTooltip />} />
      <Legend wrapperStyle={{ paddingTop: '12px' }} iconType="circle" />
      {renderSeries(Line)}
    </LineChart>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 8 }}
      transition={{ duration: 0.35 }}
      className={`lsc-chart lsc-chart--waveform${showArea ? ' lsc-chart--area' : ''}`}
    >
      {title && (
        <div className="lsc-chart-header">
          <div className="lsc-chart-icon">
            <ChartIcon size={14} />
          </div>
          <h4 className="lsc-chart-title">{title}</h4>
        </div>
      )}

      <div className="lsc-chart-body" style={{ height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartContent}
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default LiveStatsChart;
